import React, { useState, useRef } from 'react';
import { Paperclip, FileText, Image as ImageIcon, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { fileUrl } from '../services/api';
import { toast } from 'sonner';

const isImage = (ct) => ct && ct.startsWith('image/');

export const AttachmentList = ({ items = [], compact = false }) => {
  if (!items?.length) return null;
  return (
    <div className={`grid ${compact ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3'} gap-2 mt-2`}>
      {items.map(a => (
        <a
          key={a.id || a.url}
          href={fileUrl(a.url)}
          target="_blank"
          rel="noopener noreferrer"
          className="group block bg-slate-50 border border-slate-200 rounded overflow-hidden hover:border-blue-700 transition-colors"
          title={a.filename}
        >
          {isImage(a.contentType) ? (
            <img src={fileUrl(a.url)} alt={a.filename} className="h-20 w-full object-cover" />
          ) : (
            <div className="h-20 flex items-center justify-center bg-slate-100"><FileText className="h-7 w-7 text-slate-500" /></div>
          )}
          <div className="px-2 py-1 text-[11px] text-slate-600 truncate">{a.filename}</div>
        </a>
      ))}
    </div>
  );
};

/**
 * Inline attachment uploader.
 * Props:
 *  - onUpload(file): async function returning attachment record
 *  - existing: array of already-attached items to display
 *  - label: button label (default Add file)
 */
export const AttachmentUploader = ({ onUpload, existing = [], label = 'Attach file' }) => {
  const [list, setList] = useState(existing);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10 MB)'); return; }
    setBusy(true);
    try {
      const att = await onUpload(file);
      setList(prev => [...prev, att]);
      toast.success('File attached');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (id) => setList(prev => prev.filter(a => (a.id || a.url) !== id));

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Paperclip className="h-4 w-4 mr-2" />}
          {busy ? 'Uploading...' : label}
        </Button>
        <span className="text-xs text-slate-500">JPG, PNG, PDF up to 10 MB</span>
      </div>
      {list.length > 0 && (
        <div className="mt-3 space-y-2">
          {list.map(a => (
            <div key={a.id || a.url} className="flex items-center gap-3 p-2 bg-slate-50 border border-slate-200 rounded">
              {isImage(a.contentType)
                ? <img src={fileUrl(a.url)} alt="" className="h-10 w-10 object-cover rounded" />
                : <div className="h-10 w-10 bg-slate-200 rounded flex items-center justify-center"><FileText className="h-5 w-5 text-slate-600" /></div>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{a.filename}</div>
                <div className="text-xs text-slate-500">{a.size ? `${(a.size / 1024).toFixed(0)} KB` : ''}</div>
              </div>
              <a href={fileUrl(a.url)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 hover:text-blue-800">View</a>
              <button type="button" onClick={() => remove(a.id || a.url)} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
